import { ISoccerMatchListItem, SoccerMatchStatus } from "../../../types";
import { useAppStore } from "../stores/useAppStore";

interface PersistedMatchData {
  matchId: string;
  matchDateTime: string;
  status: SoccerMatchStatus;
  tournamentId: string;
  timestamp: number; // When this was stored
}

const STORAGE_KEY = "lfp_recent_match";
const STORAGE_EXPIRY_HOURS = 24;

/**
 * Store match information in local storage
 */
export function persistMatch(
  match: ISoccerMatchListItem,
  tournamentId: string
): void {
  try {
    const data: PersistedMatchData = {
      matchId: match.id,
      matchDateTime: match.matchDateTime,
      status: match.status,
      tournamentId,
      timestamp: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn("Failed to persist match:", error);
  }
}

/**
 * Get persisted match ID if it's still valid
 */
export function getPersistedMatchId(
  currentTournamentId: string
): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const data: PersistedMatchData = JSON.parse(stored);

    if (validatePersistedMatchData(data, currentTournamentId)) {
      return data.matchId;
    }

    return null;
  } catch (error) {
    console.warn("Failed to get persisted match:", error);
    clearPersistedMatch();
    return null;
  }
}

/**
 * Clear persisted match from storage
 */
export function clearPersistedMatch(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear persisted match:", error);
  }
}

/**
 * Check if a match is finished based on status and time
 */
function isMatchFinished(data: PersistedMatchData): boolean {
  const finishedStatuses = [
    SoccerMatchStatus.PLAYED,
    SoccerMatchStatus.CANCELLED,
    SoccerMatchStatus.POSTPONED,
    SoccerMatchStatus.AWARDED,
  ];

  // If status indicates match is finished
  if (finishedStatuses.includes(data.status)) {
    return true;
  }

  // If match was playing but enough time has passed (assume 2.5 hours max match duration)
  if (data.status === SoccerMatchStatus.PLAYING) {
    const matchTime = new Date(data.matchDateTime).getTime();
    const maxMatchDuration = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
    const now = Date.now();

    if (now > matchTime + maxMatchDuration) {
      return true;
    }
  }

  return false;
}

/**
 * Validate if a persisted match is still appropriate to load
 * This is called before actually using the persisted match ID
 */

function validatePersistedMatchData(
  data: PersistedMatchData,
  currentTournamentId: string,
  matchId?: string
): boolean {
  // Check if data is expired (24 hours)
  const isExpired =
    Date.now() - data.timestamp > STORAGE_EXPIRY_HOURS * 60 * 60 * 1000;
  if (isExpired) {
    clearPersistedMatch();
    return false;
  }

  // Check if tournament matches
  if (data.tournamentId !== currentTournamentId) {
    clearPersistedMatch();
    return false;
  }

  // Check if specific matchId is provided and matches
  if (matchId && data.matchId !== matchId) {
    return false;
  }

  // Check if match is still valid (not finished)
  if (isMatchFinished(data)) {
    clearPersistedMatch();
    return false;
  }

  return true;
}

/**
 * Update persisted match when user switches matches
 * Only persist if there are multiple live matches to avoid unnecessary storage
 */
export function updatePersistedMatchOnSwitch(
  match: ISoccerMatchListItem,
  tournamentId: string
): void {
  const hasMultipleLiveMatches = useAppStore.getState().hasMultipleLiveMatches;

  if (hasMultipleLiveMatches) {
    persistMatch(match, tournamentId);
  } else {
    // Clear storage if there's only one or no live matches
    clearPersistedMatch();
  }
}
